import React, { useEffect, useRef, useState } from "react";
import { geocodeByPlaceId } from "react-google-places-autocomplete";
import CustomGooglePlacesAutocomplete from "components/Common/auto-complete";
import { environment } from "environment/environment";
import MerchantLoader from "./MerchantLoader";
import MerchantPricingTab from "./MerchantPricingTab";
import LabelSizePicker from "./LabelSizePicker";
import {
  DEFAULT_LABEL_FORMAT,
  LabelFormatId,
  LabelFormatOption,
  mergeLabelFormats,
} from "./labelFormats";
import { isLiveMerchantPlatform } from "./merchantBranding";

export type { LabelFormatId, LabelFormatOption };

export interface PickupAddressForm {
  street: string;
  unit: string;
  city: string;
  postalCode: string;
  lat: number | null;
  lng: number | null;
  contactName: string;
  contactPhone: string;
  companyName: string;
}

interface DeliveryMethodOption {
  id: string;
  label: string;
}

interface SettingsPayload {
  labelFormat?: LabelFormatId;
  labelFormats?: LabelFormatOption[];
  profileComplete?: boolean;
  defaultPickupAddress?: Partial<PickupAddressForm> | null;
  defaultDeliveryMethod?: string | null;
  orderPreparationMinutes?: number;
  deliveryMethods?: DeliveryMethodOption[];
}

const FALLBACK_METHODS: DeliveryMethodOption[] = [
  { id: "drop_at_door", label: "Drop at door" },
  { id: "drop_at_mailbox", label: "Drop at mailbox" },
  { id: "drop_at_front_desk", label: "Drop at front desk" },
  { id: "return", label: "Return if delivery fails" },
  { id: "reattempt", label: "Reattempt if delivery fails" },
  { id: "call_me_to_confirm", label: "Call to confirm" },
];

const EMPTY_ADDRESS: PickupAddressForm = {
  street: "",
  unit: "",
  city: "",
  postalCode: "",
  lat: null,
  lng: null,
  contactName: "",
  contactPhone: "",
  companyName: "",
};

const PREPARATION_OPTIONS = [
  { minutes: 0, label: "Ready immediately" },
  { minutes: 30, label: "30 minutes" },
  { minutes: 60, label: "60 minutes" },
  { minutes: 90, label: "90 minutes" },
  { minutes: 120, label: "120 minutes" },
];

function preparationMinutesFrom(value: unknown): number {
  const minutes = Number(value);
  return PREPARATION_OPTIONS.some((option) => option.minutes === minutes) ? minutes : 0;
}

const addressSearchOptions = {
  componentRestrictions: { country: "ca" },
};

type SettingsTab = "profile" | "pricing" | "notifications" | "labels";
type SaveStatus = "idle" | "saving" | "saved" | "error";

interface MerchantSettingsProps {
  platform: string;
  shop: string;
  session: string;
  storeName?: string;
  initialFormat?: LabelFormatId;
  initialFormats?: LabelFormatOption[];
  onFormatChange?: (next: LabelFormatId) => void;
}

const toAddress = (value?: Partial<PickupAddressForm> | null): PickupAddressForm => ({
  ...EMPTY_ADDRESS,
  ...(value || {}),
  street: value?.street || "",
  unit: value?.unit || "",
  city: value?.city || "",
  postalCode: value?.postalCode || "",
  lat: typeof value?.lat === "number" ? value.lat : null,
  lng: typeof value?.lng === "number" ? value.lng : null,
  contactName: value?.contactName || "",
  contactPhone: value?.contactPhone || "",
  companyName: value?.companyName || "",
});

const isProfileComplete = (
  address: PickupAddressForm,
  method: string
): boolean =>
  !!address.street.trim() &&
  !!address.city.trim() &&
  typeof address.lat === "number" &&
  typeof address.lng === "number" &&
  !!method;

const formatAddress = (address: PickupAddressForm): string =>
  [address.street, address.city, address.postalCode]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");

const samePickup = (left: PickupAddressForm, right: PickupAddressForm): boolean =>
  left.street.trim() === right.street.trim() &&
  left.unit.trim() === right.unit.trim() &&
  left.city.trim() === right.city.trim() &&
  left.postalCode.trim().toUpperCase() === right.postalCode.trim().toUpperCase() &&
  left.contactName.trim() === right.contactName.trim() &&
  left.contactPhone.trim() === right.contactPhone.trim() &&
  left.companyName.trim() === right.companyName.trim() &&
  left.lat === right.lat &&
  left.lng === right.lng;

const pickupPayload = (address: PickupAddressForm) => ({
  street: address.street.trim(),
  unit: address.unit.trim(),
  city: address.city.trim(),
  postalCode: address.postalCode.trim().toUpperCase(),
  lat: address.lat,
  lng: address.lng,
  contactName: address.contactName.trim(),
  contactPhone: address.contactPhone.trim(),
  companyName: address.companyName.trim(),
});

const MerchantSettings: React.FC<MerchantSettingsProps> = ({
  platform,
  shop,
  session,
  storeName,
  initialFormat,
  initialFormats,
  onFormatChange,
}) => {
  const [tab, setTab] = useState<SettingsTab>("profile");
  const [format, setFormat] = useState<LabelFormatId>(initialFormat || DEFAULT_LABEL_FORMAT);
  const [formats, setFormats] = useState<LabelFormatOption[]>(
    mergeLabelFormats(initialFormats, initialFormat)
  );
  const [address, setAddress] = useState<PickupAddressForm>(EMPTY_ADDRESS);
  const [savedAddress, setSavedAddress] = useState<PickupAddressForm>(EMPTY_ADDRESS);
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const [savedMethod, setSavedMethod] = useState("");
  const [preparationMinutes, setPreparationMinutes] = useState(0);
  const [savedPreparationMinutes, setSavedPreparationMinutes] = useState(0);
  const [deliveryMethods, setDeliveryMethods] = useState<DeliveryMethodOption[]>(FALLBACK_METHODS);
  const [profileComplete, setProfileComplete] = useState(false);
  const [profileStatus, setProfileStatus] = useState<SaveStatus>("idle");
  const [profileError, setProfileError] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchKey, setSearchKey] = useState(0);

  const settingsUrl = () => {
    const params = new URLSearchParams({ session });
    if (shop) {
      params.set("shop", shop);
    }
    return `${environment.api_url}/api/v1/integrations/${platform}/settings?${params.toString()}`;
  };

  const applyPayload = (data: SettingsPayload) => {
    const nextFormat = data.labelFormat || format;
    if (data.labelFormat) {
      setFormat(data.labelFormat);
      if (onFormatChange) {
        onFormatChange(data.labelFormat);
      }
    }
    setFormats(mergeLabelFormats(data.labelFormats, nextFormat));
    if (data.deliveryMethods && data.deliveryMethods.length) {
      setDeliveryMethods(data.deliveryMethods);
    }
    const nextAddress = toAddress(data.defaultPickupAddress);
    const nextMethod = data.defaultDeliveryMethod || "";
    const nextPreparation = preparationMinutesFrom(data.orderPreparationMinutes);
    setSavedAddress(nextAddress);
    setSavedMethod(nextMethod);
    setSavedPreparationMinutes(nextPreparation);
    setAddress(nextAddress);
    setDeliveryMethod(nextMethod);
    setPreparationMinutes(nextPreparation);
    setProfileComplete(
      typeof data.profileComplete === "boolean"
        ? data.profileComplete
        : isProfileComplete(nextAddress, nextMethod)
    );
  };

  useEffect(() => {
    if (initialFormat) {
      setFormat(initialFormat);
    }
    setFormats(mergeLabelFormats(initialFormats, initialFormat || format));
  }, [initialFormat, initialFormats]);

  useEffect(() => {
    if (!session || !isLiveMerchantPlatform(platform)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(settingsUrl())
      .then((response) => response.json())
      .then((body) => {
        if (cancelled || body.success === false) {
          return;
        }
        applyPayload((body.data || {}) as SettingsPayload);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [platform, shop, session]);

  const selectPickupAddress = async (label: string, placeId: string) => {
    if (!placeId) {
      return;
    }
    const labelParts = String(label || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    try {
      const [place] = await geocodeByPlaceId(placeId);
      const component = (type: string) =>
        place.address_components.find((item) => item.types.includes(type))?.long_name || "";
      const street =
        [component("street_number"), component("route")].filter(Boolean).join(" ") ||
        place.formatted_address?.split(",")[0] ||
        labelParts[0] ||
        "";
      const city =
        component("locality") ||
        component("postal_town") ||
        component("sublocality") ||
        component("administrative_area_level_3") ||
        labelParts[1] ||
        "";
      const postalCode = (component("postal_code") || "").toUpperCase();
      setAddress((current) => ({
        ...current,
        street: street || current.street,
        city: city || current.city,
        postalCode: postalCode || current.postalCode,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      }));
      setProfileStatus("idle");
      setProfileError("");
    } catch {
      if (!labelParts.length) {
        setProfileError("Could not read that pickup address. Search again and pick a result.");
        return;
      }
      setAddress((current) => ({
        ...current,
        street: labelParts[0] || current.street,
        city: labelParts[1] || current.city,
        lat: null,
        lng: null,
      }));
      setProfileError("");
    }
  };

  const clearPickupAddress = () => {
    setAddress((current) => ({
      ...EMPTY_ADDRESS,
      contactName: current.contactName,
      contactPhone: current.contactPhone,
      companyName: current.companyName,
    }));
    setSearchKey((current) => current + 1);
    setProfileStatus("idle");
    setProfileError("");
  };

  const saveProfile = async (event: React.FormEvent) => {
    event.preventDefault();
    setProfileError("");
    if (!address.street.trim() || !address.city.trim()) {
      setProfileError("Search and select a pickup address.");
      return;
    }
    if (!deliveryMethod) {
      setProfileError("Choose a default delivery method before saving the pickup details.");
      return;
    }
    setProfileStatus("saving");
    try {
      const response = await fetch(settingsUrl(), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          section: "profile",
          defaultDeliveryMethod: deliveryMethod,
          defaultPickupAddress: pickupPayload(address),
          orderPreparationMinutes: preparationMinutes,
        }),
      });
      const body = await response.json();
      if (!response.ok || body.success === false) {
        throw new Error(body.error || body.message || "Could not save store settings");
      }
      applyPayload((body.data || {}) as SettingsPayload);
      setProfileStatus("saved");
    } catch (error) {
      setProfileStatus("error");
      setProfileError(
        error instanceof Error ? error.message : "Could not save store settings. Try again."
      );
    }
  };

  const profileDirty =
    !samePickup(address, savedAddress) ||
    deliveryMethod !== savedMethod ||
    preparationMinutes !== savedPreparationMinutes;

  if (loading) {
    return (
      <section className="zulu-plugin__stage-body">
        <MerchantLoader size="panel" label="Loading settings…" />
      </section>
    );
  }

  return (
    <section className="zulu-plugin__stage-body">
      <div className="zulu-plugin__settings">
        <div className="zulu-plugin__settings-tabs" role="tablist" aria-label="Settings">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "profile"}
            className={tab === "profile" ? "is-active" : ""}
            onClick={() => setTab("profile")}
          >
            Store settings
            {!profileComplete ? <span className="zulu-plugin__settings-tab-dot" /> : null}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "pricing"}
            className={tab === "pricing" ? "is-active" : ""}
            onClick={() => setTab("pricing")}
          >
            Pricing
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "notifications"}
            className={tab === "notifications" ? "is-active" : ""}
            onClick={() => setTab("notifications")}
          >
            Notifications
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "labels"}
            className={tab === "labels" ? "is-active" : ""}
            onClick={() => setTab("labels")}
          >
            Label format
          </button>
        </div>

        <div className="zulu-plugin__settings-pane">
        {!profileComplete ? (
          <p className="zulu-plugin__settings-banner" role="status">
            Complete your store settings
          </p>
        ) : null}

        {tab === "pricing" ? (
          <MerchantPricingTab platform={platform} shop={shop} session={session} />
        ) : null}

        {tab === "notifications" ? (
          <MerchantNotificationSettingsTab
            platform={platform}
            shop={shop}
            session={session}
            fallbackStoreName={storeName || ""}
          />
        ) : null}

        {tab === "profile" ? (
          <form className="zulu-plugin__settings-card" onSubmit={saveProfile}>
            <p className="zulu-plugin__settings-kicker">Store defaults</p>
            <h2>Store settings</h2>

            <label className="zulu-plugin__field-label" htmlFor="pickup-search">
              Default pickup address
            </label>
            {address.street.trim() ? (
              <div className="zulu-plugin__address-picked">
                <div>
                  <strong>Selected pickup</strong>
                  <p>{formatAddress(address)}</p>
                </div>
                <button
                  type="button"
                  className="zulu-plugin__address-clear"
                  aria-label="Clear pickup address"
                  onClick={clearPickupAddress}
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="zulu-plugin__address-search" id="pickup-search">
                <CustomGooglePlacesAutocomplete
                  key={searchKey}
                  apiKey={environment.google_maps_key}
                  autocompletionRequest={addressSearchOptions}
                  addressType="pickup"
                  handleAddress={selectPickupAddress}
                />
              </div>
            )}

            <div className="zulu-plugin__field-grid">
              <label>
                Contact name
                <input
                  value={address.contactName}
                  maxLength={80}
                  onChange={(event) => {
                    setAddress((current) => ({ ...current, contactName: event.target.value }));
                    setProfileStatus("idle");
                    setProfileError("");
                  }}
                />
              </label>
              <label>
                Contact phone
                <input
                  value={address.contactPhone}
                  maxLength={24}
                  onChange={(event) => {
                    setAddress((current) => ({ ...current, contactPhone: event.target.value }));
                    setProfileStatus("idle");
                    setProfileError("");
                  }}
                />
              </label>
            </div>

            <p className="zulu-plugin__field-label">Default delivery method</p>
            <div className="zulu-plugin__label-formats" role="radiogroup" aria-label="Default delivery method">
              {deliveryMethods.map((option) => {
                const selected = deliveryMethod === option.id;
                return (
                  <label
                    key={option.id}
                    className={`zulu-plugin__label-format${selected ? " is-selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="delivery-method"
                      value={option.id}
                      checked={selected}
                      disabled={profileStatus === "saving"}
                      onChange={() => {
                        setDeliveryMethod(option.id);
                        setProfileStatus("idle");
                        setProfileError("");
                      }}
                    />
                    <span className="zulu-plugin__label-format-copy">
                      <strong>{option.label}</strong>
                    </span>
                  </label>
                );
              })}
            </div>

            <p className="zulu-plugin__field-label">Order preparation time</p>
            <p className="zulu-plugin__settings-help">
              Pickup is ready at the order time plus this preparation time.
              Order time moves forward to the next 15-minute step. If preparation does not finish
              before the delivery window closes, pickup moves to the next day
              that window is open.
            </p>
            <div
              className="zulu-plugin__label-formats"
              role="radiogroup"
              aria-label="Order preparation time"
            >
              {PREPARATION_OPTIONS.map((option) => {
                const selected = preparationMinutes === option.minutes;
                return (
                  <label
                    key={option.minutes}
                    className={`zulu-plugin__label-format${selected ? " is-selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="preparation-time"
                      value={option.minutes}
                      checked={selected}
                      disabled={profileStatus === "saving"}
                      onChange={() => {
                        setPreparationMinutes(option.minutes);
                        setProfileStatus("idle");
                        setProfileError("");
                      }}
                    />
                    <span className="zulu-plugin__label-format-copy">
                      <strong>{option.label}</strong>
                    </span>
                  </label>
                );
              })}
            </div>
            {profileDirty || profileStatus === "saving" ? (
              <div className="zulu-plugin__settings-actions">
                <button
                  type="submit"
                  className="zulu-plugin__settings-save"
                  disabled={profileStatus === "saving"}
                >
                  {profileStatus === "saving" ? "Saving…" : "Save"}
                </button>
              </div>
            ) : null}
            {profileStatus === "saved" ? (
              <p className="zulu-plugin__settings-status is-ok">Store settings saved.</p>
            ) : null}
            {profileError || profileStatus === "error" ? (
              <p className="zulu-plugin__settings-status is-error">
                {profileError || "Could not save. Try again."}
              </p>
            ) : null}
          </form>
        ) : null}

        {tab === "labels" ? (
          <article className="zulu-plugin__settings-card">
            <p className="zulu-plugin__settings-kicker">Label settings</p>
            <h2>Label size</h2>
            <p className="zulu-plugin__settings-help">
              4 × 6 in is the standard shipping label. Pick another size if your
              printer uses square, compact, extended, or Letter paper. New labels
              use this size.
            </p>
            <LabelSizePicker
              platform={platform}
              shop={shop}
              session={session}
              format={format}
              formats={formats}
              onFormatChange={(next) => {
                setFormat(next);
                if (onFormatChange) {
                  onFormatChange(next);
                }
              }}
              onFormatsChange={setFormats}
            />
          </article>
        ) : null}
        </div>
      </div>
    </section>
  );
};

const ORDER_STATUS_TOKEN = "[Order Status]";
const ORDER_NUMBER_TOKEN = "[Order Number]";
const STORE_NAME_TOKEN = "[Store Name]";
const REPLY_TO_TOKEN = "[Reply-To Email]";

const DEFAULT_DELIVERY_EMAIL_TEMPLATE = `Hi there,
Your order from [Store Name] has an update: [Order Status].
Order [Order Number]

If you have any questions about your order, please reply to this email or contact the store at [Reply-To Email].

Thank you,
[Store Name]`;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const hasToken = (text: string, token: string): boolean =>
  text.toLowerCase().includes(token.toLowerCase());

const keepRequiredOrderTokens = (
  nextValue: string,
  previousValue: string
): string => {
  if (
    !hasToken(nextValue, ORDER_STATUS_TOKEN) &&
    hasToken(previousValue, ORDER_STATUS_TOKEN)
  ) {
    return previousValue;
  }
  if (
    !hasToken(nextValue, ORDER_NUMBER_TOKEN) &&
    hasToken(previousValue, ORDER_NUMBER_TOKEN)
  ) {
    return previousValue;
  }
  return nextValue;
};

const ensureOrderTokens = (template: string): string => {
  let next = String(template || "").replace(/\r\n/g, "\n").trim();
  if (!next) {
    return DEFAULT_DELIVERY_EMAIL_TEMPLATE;
  }
  if (!hasToken(next, ORDER_STATUS_TOKEN)) {
    next = `${next}\n\nYour order from [Store Name] has an update: ${ORDER_STATUS_TOKEN}.`;
  }
  if (!hasToken(next, ORDER_NUMBER_TOKEN)) {
    next = `${next}\nOrder ${ORDER_NUMBER_TOKEN}`;
  }
  if (!hasToken(next, STORE_NAME_TOKEN)) {
    next = `${next}\n\nThank you,\n${STORE_NAME_TOKEN}`;
  }
  if (!hasToken(next, REPLY_TO_TOKEN)) {
    next = `${next}\n\nContact us at ${REPLY_TO_TOKEN}.`;
  }
  return next.trim();
};

const escapeHtml = (value: string): string =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const tokenLabelHtml = (
  token: string,
  label: string,
  className = "zulu-plugin__notice-token-label"
): string =>
  `<span class="${className}" contenteditable="false" data-token="${token}">${escapeHtml(
    label
  )}</span>`;

const templateToEditorHtml = (
  template: string,
  storeName: string,
  replyToEmail: string
): string =>
  escapeHtml(template || DEFAULT_DELIVERY_EMAIL_TEMPLATE)
    .replace(/\n/g, "<br>")
    .replace(
      /\[Order Status\]/gi,
      tokenLabelHtml("order-status", "Order Status")
    )
    .replace(
      /\[Order Number\]/gi,
      tokenLabelHtml("order-number", "Order Number")
    )
    .replace(
      /\[Store Name\]/gi,
      tokenLabelHtml(
        "store-name",
        storeName.trim() || "Store Name",
        "zulu-plugin__notice-plain-token"
      )
    )
    .replace(
      /\[Reply-To Email\]/gi,
      tokenLabelHtml(
        "reply-to",
        replyToEmail.trim() || "Reply-To Email",
        "zulu-plugin__notice-plain-token"
      )
    );

const editorNodeToTemplate = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || "";
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }
  const el = node as HTMLElement;
  const token = el.getAttribute("data-token");
  if (token === "order-status") return ORDER_STATUS_TOKEN;
  if (token === "order-number") return ORDER_NUMBER_TOKEN;
  if (token === "store-name") return STORE_NAME_TOKEN;
  if (token === "reply-to") return REPLY_TO_TOKEN;
  if (el.tagName === "BR") return "\n";
  const inner = Array.from(el.childNodes).map(editorNodeToTemplate).join("");
  if (el.tagName === "DIV" || el.tagName === "P") {
    return `${inner}\n`;
  }
  return inner;
};

const editorHtmlToTemplate = (root: HTMLElement): string =>
  Array.from(root.childNodes)
    .map(editorNodeToTemplate)
    .join("")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^\n+|\n+$/g, "");

const EmailBodyEditor: React.FC<{
  value: string;
  disabled?: boolean;
  storeName: string;
  replyToEmail: string;
  onChange: (value: string) => void;
}> = ({ value, disabled, storeName, replyToEmail, onChange }) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const lastValueRef = useRef(value);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    // Always refresh labels when store name / reply-to arrive, even if focused.
    const html = templateToEditorHtml(value, storeName, replyToEmail);
    const selection = document.getSelection();
    const restoreCaret =
      document.activeElement === editor && selection && selection.rangeCount > 0;
    const range = restoreCaret ? selection.getRangeAt(0).cloneRange() : null;
    if (editor.innerHTML !== html) {
      editor.innerHTML = html;
      if (restoreCaret && range) {
        try {
          selection.removeAllRanges();
          selection.addRange(range);
        } catch {
          // Ignore caret restore failures after HTML refresh.
        }
      }
    }
    lastValueRef.current = value;
  }, [value, storeName, replyToEmail]);

  const commitFromEditor = (ensureTokens: boolean) => {
    const editor = editorRef.current;
    if (!editor) return;
    let next = editorHtmlToTemplate(editor);
    next = keepRequiredOrderTokens(next, lastValueRef.current);
    if (ensureTokens) {
      next = ensureOrderTokens(next);
    }
    if (
      !hasToken(next, ORDER_STATUS_TOKEN) ||
      !hasToken(next, ORDER_NUMBER_TOKEN)
    ) {
      editor.innerHTML = templateToEditorHtml(
        lastValueRef.current,
        storeName,
        replyToEmail
      );
      return;
    }
    if (next !== lastValueRef.current) {
      lastValueRef.current = next;
      onChange(next);
    } else if (ensureTokens) {
      editor.innerHTML = templateToEditorHtml(next, storeName, replyToEmail);
    }
  };

  return (
    <div
      id="delivery-email-template"
      ref={editorRef}
      className="zulu-plugin__notice-template is-rich"
      contentEditable={!disabled}
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label="Customer email body"
      onInput={() => commitFromEditor(false)}
      onBlur={() => commitFromEditor(true)}
    />
  );
};

const DEFAULT_LOGO_CONSTRAINTS = {
  allowedFormats: ["PNG", "JPG", "JPEG", "GIF"],
  maxBytes: 512 * 1024,
  maxWidthPx: 1200,
  maxHeightPx: 400,
  recommendedWidthPx: 600,
  recommendedHeightPx: 200,
};

interface NotificationLogoConstraints {
  allowedFormats: string[];
  maxBytes: number;
  maxWidthPx: number;
  maxHeightPx: number;
  recommendedWidthPx: number;
  recommendedHeightPx: number;
}

interface NotificationSettingsForm {
  customerNotificationsEnabled: boolean;
  replyToEmail: string;
  deliveryEmailTemplate: string;
  fromEmail: string;
  storeName: string;
  storeEmail: string;
  logoUrl: string;
  logoFileName: string;
  logoConstraints: NotificationLogoConstraints;
}

const EMPTY_NOTIFICATION_SETTINGS: NotificationSettingsForm = {
  customerNotificationsEnabled: true,
  replyToEmail: "",
  deliveryEmailTemplate: DEFAULT_DELIVERY_EMAIL_TEMPLATE,
  fromEmail: "notifications@zulucourier.com",
  storeName: "",
  storeEmail: "",
  logoUrl: "",
  logoFileName: "",
  logoConstraints: DEFAULT_LOGO_CONSTRAINTS,
};

const parseNotificationSettings = (value: unknown): NotificationSettingsForm => {
  const raw = (value || {}) as Partial<NotificationSettingsForm>;
  const storeEmail = String(raw.storeEmail || "").trim().toLowerCase();
  const replyToEmail =
    String(raw.replyToEmail || "").trim() || storeEmail;
  const constraints = {
    ...DEFAULT_LOGO_CONSTRAINTS,
    ...(raw.logoConstraints || {}),
  };
  return {
    customerNotificationsEnabled: raw.customerNotificationsEnabled !== false,
    replyToEmail,
    deliveryEmailTemplate: ensureOrderTokens(
      String(raw.deliveryEmailTemplate || "").trim() ||
        DEFAULT_DELIVERY_EMAIL_TEMPLATE
    ),
    fromEmail:
      String(raw.fromEmail || "").trim() || "notifications@zulucourier.com",
    storeName: String(raw.storeName || "").trim(),
    storeEmail,
    logoUrl: String(raw.logoUrl || "").trim(),
    logoFileName: String(raw.logoFileName || "").trim(),
    logoConstraints: {
      allowedFormats:
        Array.isArray(constraints.allowedFormats) && constraints.allowedFormats.length
          ? constraints.allowedFormats.map(String)
          : DEFAULT_LOGO_CONSTRAINTS.allowedFormats,
      maxBytes: Number(constraints.maxBytes) || DEFAULT_LOGO_CONSTRAINTS.maxBytes,
      maxWidthPx:
        Number(constraints.maxWidthPx) || DEFAULT_LOGO_CONSTRAINTS.maxWidthPx,
      maxHeightPx:
        Number(constraints.maxHeightPx) || DEFAULT_LOGO_CONSTRAINTS.maxHeightPx,
      recommendedWidthPx:
        Number(constraints.recommendedWidthPx) ||
        DEFAULT_LOGO_CONSTRAINTS.recommendedWidthPx,
      recommendedHeightPx:
        Number(constraints.recommendedHeightPx) ||
        DEFAULT_LOGO_CONSTRAINTS.recommendedHeightPx,
    },
  };
};

const sameNotificationSettings = (
  left: NotificationSettingsForm,
  right: NotificationSettingsForm
): boolean =>
  left.customerNotificationsEnabled === right.customerNotificationsEnabled &&
  left.replyToEmail.trim() === right.replyToEmail.trim() &&
  left.deliveryEmailTemplate === right.deliveryEmailTemplate;

const LOGO_ACCEPT_EXTENSIONS = [".png", ".jpg", ".jpeg", ".gif"];
const LOGO_ACCEPT_MIME = ["image/png", "image/jpeg", "image/jpg", "image/gif"];

const logoErrorFromCode = (
  code: string,
  message: string,
  constraints: NotificationLogoConstraints
): string => {
  const maxKb = Math.round((constraints.maxBytes || 512 * 1024) / 1024);
  if (code === "NOTIFICATION_LOGO_TOO_LARGE") {
    return `Logo must be ${maxKb} KB or smaller.`;
  }
  if (code === "NOTIFICATION_LOGO_FORMAT_INVALID") {
    return message || "Use a PNG, JPG, or GIF logo.";
  }
  if (code === "NOTIFICATION_LOGO_DIMENSIONS_INVALID") {
    return (
      message ||
      `Logo must be at most ${constraints.maxWidthPx}×${constraints.maxHeightPx} pixels.`
    );
  }
  if (code === "NOTIFICATION_LOGO_EMPTY" || code === "NOTIFICATION_LOGO_REQUIRED") {
    return message || "Choose a logo image to upload.";
  }
  return message;
};

const readImageDimensions = (
  file: File
): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      URL.revokeObjectURL(url);
      if (!width || !height) {
        reject(new Error("Could not read the logo image size."));
        return;
      }
      resolve({ width, height });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file is not a valid logo image."));
    };
    image.src = url;
  });

const validateLogoFileLocally = async (
  file: File,
  constraints: NotificationLogoConstraints
): Promise<string | null> => {
  const maxBytes = constraints.maxBytes || DEFAULT_LOGO_CONSTRAINTS.maxBytes;
  const maxKb = Math.round(maxBytes / 1024);
  const name = String(file.name || "").toLowerCase();
  const extension = name.includes(".") ? `.${name.split(".").pop()}` : "";
  const mime = String(file.type || "").toLowerCase();

  if (
    !LOGO_ACCEPT_EXTENSIONS.includes(extension) &&
    !LOGO_ACCEPT_MIME.includes(mime)
  ) {
    return "Use a PNG, JPG, or GIF logo.";
  }
  if (extension && !LOGO_ACCEPT_EXTENSIONS.includes(extension)) {
    return "Use a PNG, JPG, or GIF logo.";
  }
  if (mime && !LOGO_ACCEPT_MIME.includes(mime) && mime !== "application/octet-stream") {
    return "Use a PNG, JPG, or GIF logo.";
  }
  if (!file.size) {
    return "The logo file is empty.";
  }
  if (file.size > maxBytes) {
    return `Logo must be ${maxKb} KB or smaller.`;
  }

  try {
    const size = await readImageDimensions(file);
    if (
      size.width > constraints.maxWidthPx ||
      size.height > constraints.maxHeightPx
    ) {
      return `Logo must be at most ${constraints.maxWidthPx}×${constraints.maxHeightPx} pixels.`;
    }
  } catch (error) {
    return error instanceof Error
      ? error.message
      : "That file is not a valid logo image.";
  }
  return null;
};

const MerchantNotificationSettingsTab: React.FC<{
  platform: string;
  shop: string;
  session: string;
  fallbackStoreName?: string;
}> = ({ platform, shop, session, fallbackStoreName = "" }) => {
  const [settings, setSettings] = useState<NotificationSettingsForm>(
    EMPTY_NOTIFICATION_SETTINGS
  );
  const [savedSettings, setSavedSettings] = useState<NotificationSettingsForm>(
    EMPTY_NOTIFICATION_SETTINGS
  );
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [error, setError] = useState("");
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState("");
  const logoInputRef = useRef<HTMLInputElement | null>(null);

  const notificationsUrl = () => {
    const params = new URLSearchParams({ session });
    if (shop) {
      params.set("shop", shop);
    }
    return `${environment.api_url}/api/v1/integrations/${platform}/notifications?${params.toString()}`;
  };

  const logoUrl = () => {
    const params = new URLSearchParams({ session });
    if (shop) {
      params.set("shop", shop);
    }
    return `${environment.api_url}/api/v1/integrations/${platform}/notifications/logo?${params.toString()}`;
  };

  useEffect(() => {
    if (!session || !isLiveMerchantPlatform(platform)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(notificationsUrl())
      .then((response) => response.json())
      .then((body) => {
        if (cancelled || body.success === false) {
          return;
        }
        const next = parseNotificationSettings(body.data && body.data.settings);
        const withName = {
          ...next,
          storeName: next.storeName || fallbackStoreName.trim(),
        };
        setSettings(withName);
        setSavedSettings(withName);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [platform, shop, session, fallbackStoreName]);

  const saveSettings = async (event: React.FormEvent) => {
    event.preventDefault();
    const replyTo = settings.replyToEmail.trim();
    if (settings.customerNotificationsEnabled) {
      if (!replyTo) {
        setError("Enter a reply-to email so customers can reach your store.");
        setStatus("error");
        return;
      }
      if (!EMAIL_PATTERN.test(replyTo)) {
        setError("Enter a valid reply-to email address.");
        setStatus("error");
        return;
      }
      if (!settings.deliveryEmailTemplate.trim()) {
        setError("Enter a customer email message.");
        setStatus("error");
        return;
      }
    }
    setError("");
    setStatus("saving");
    try {
      const response = await fetch(notificationsUrl(), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerNotificationsEnabled: settings.customerNotificationsEnabled,
          replyToEmail: replyTo,
          deliveryEmailTemplate: ensureOrderTokens(
            settings.deliveryEmailTemplate
          ),
        }),
      });
      const body = await response.json();
      if (!response.ok || body.success === false) {
        throw new Error(body.error || body.message || "Could not save.");
      }
      const next = parseNotificationSettings(body.data);
      const withName = {
        ...next,
        storeName: next.storeName || fallbackStoreName.trim() || settings.storeName,
      };
      setSettings(withName);
      setSavedSettings(withName);
      setStatus("saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save. Try again.");
      setStatus("error");
    }
  };

  const uploadLogo = async (file: File | null) => {
    if (!file || !emailsOn) {
      return;
    }
    const constraints = settings.logoConstraints || DEFAULT_LOGO_CONSTRAINTS;
    const localError = await validateLogoFileLocally(file, constraints);
    if (localError) {
      setLogoError(localError);
      setError(localError);
      setStatus("error");
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
      return;
    }

    setLogoBusy(true);
    setLogoError("");
    setError("");
    setStatus("idle");
    try {
      const form = new FormData();
      form.append("logo", file);
      const response = await fetch(logoUrl(), {
        method: "POST",
        body: form,
      });
      const text = await response.text();
      let body: {
        success?: boolean;
        data?: unknown;
        error?: unknown;
        message?: unknown;
        errorCode?: unknown;
      } = {};
      if (text) {
        try {
          body = JSON.parse(text);
        } catch {
          throw new Error("Could not upload the logo. Try again.");
        }
      }
      if (!response.ok || body.success === false) {
        throw new Error(
          logoErrorFromCode(
            String(body.errorCode || ""),
            String(body.error || body.message || "").trim(),
            constraints
          ) || "Could not upload the logo. Try again."
        );
      }
      const next = parseNotificationSettings(body.data);
      setSettings((current) => ({
        ...current,
        logoUrl: next.logoUrl,
        logoFileName: next.logoFileName,
        logoConstraints: next.logoConstraints,
      }));
      setSavedSettings((current) => ({
        ...current,
        logoUrl: next.logoUrl,
        logoFileName: next.logoFileName,
        logoConstraints: next.logoConstraints,
      }));
      setLogoError("");
      setStatus("saved");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not upload the logo. Try again.";
      setLogoError(message);
      setError(message);
      setStatus("error");
    } finally {
      setLogoBusy(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
    }
  };

  const removeLogo = async () => {
    if (!emailsOn || !settings.logoUrl) {
      return;
    }
    const constraints = settings.logoConstraints || DEFAULT_LOGO_CONSTRAINTS;
    setLogoBusy(true);
    setLogoError("");
    setError("");
    setStatus("idle");
    try {
      const response = await fetch(logoUrl(), { method: "DELETE" });
      const text = await response.text();
      let body: {
        success?: boolean;
        data?: unknown;
        error?: unknown;
        message?: unknown;
        errorCode?: unknown;
      } = {};
      if (text) {
        try {
          body = JSON.parse(text);
        } catch {
          throw new Error("Could not remove the logo. Try again.");
        }
      }
      if (!response.ok || body.success === false) {
        throw new Error(
          logoErrorFromCode(
            String(body.errorCode || ""),
            String(body.error || body.message || "").trim(),
            constraints
          ) || "Could not remove the logo. Try again."
        );
      }
      const next = parseNotificationSettings(body.data);
      setSettings((current) => ({
        ...current,
        logoUrl: next.logoUrl,
        logoFileName: next.logoFileName,
        logoConstraints: next.logoConstraints,
      }));
      setSavedSettings((current) => ({
        ...current,
        logoUrl: next.logoUrl,
        logoFileName: next.logoFileName,
        logoConstraints: next.logoConstraints,
      }));
      setLogoError("");
      setStatus("saved");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not remove the logo. Try again.";
      setLogoError(message);
      setError(message);
      setStatus("error");
    } finally {
      setLogoBusy(false);
    }
  };

  if (loading) {
    return <MerchantLoader size="panel" label="Loading notification settings…" />;
  }

  const emailsOn = settings.customerNotificationsEnabled === true;
  const logoLimits = settings.logoConstraints;
  const maxKb = Math.round((logoLimits.maxBytes || 512 * 1024) / 1024);

  const setEmailsOn = (on: boolean) => {
    setSettings((current) => ({
      ...current,
      customerNotificationsEnabled: on,
    }));
    setStatus("idle");
    setError("");
    setLogoError("");
  };

  return (
    <div className="zulu-plugin__settings-card">
      <p className="zulu-plugin__settings-kicker">Customer emails</p>
      <h2>Notification settings</h2>
      <p className="zulu-plugin__field-label">Customer Notifications</p>
      <div className="zulu-plugin__notice-toggle" role="radiogroup" aria-label="Customer notifications">
        <label className={`zulu-plugin__notice-toggle-option${emailsOn ? " is-on" : ""}`}>
          <input
            type="radio"
            name="customer-notifications"
            checked={emailsOn}
            onChange={() => setEmailsOn(true)}
          />
          ON
        </label>
        <label className={`zulu-plugin__notice-toggle-option${!emailsOn ? " is-on" : ""}`}>
          <input
            type="radio"
            name="customer-notifications"
            checked={!emailsOn}
            onChange={() => setEmailsOn(false)}
          />
          OFF
        </label>
      </div>
      <div
        className="zulu-plugin__notice-on-only"
        hidden={!emailsOn}
        aria-hidden={!emailsOn}
      >
        <p className="zulu-plugin__settings-help">
          When an order is Delivered, Dropped, Returned, or Reattempted, the same
          Shopify customers who got your order confirmation also get this update.
          The shop name in the subject and body matches your Shopify shop name.
          Use your logo and custom body. Replies go to your Reply-To address — not
          to Zulu. No Zulu branding.
        </p>
        <label className="zulu-plugin__field-label" htmlFor="store-email-logo">
          Store logo
        </label>
        <div className="zulu-plugin__notice-logo">
          {settings.logoUrl ? (
            <div className="zulu-plugin__notice-logo-preview">
              <img src={settings.logoUrl} alt="Store logo preview" />
            </div>
          ) : (
            <div className="zulu-plugin__notice-logo-empty">No logo uploaded</div>
          )}
          <div className="zulu-plugin__notice-logo-actions">
            <input
              id="store-email-logo"
              ref={logoInputRef}
              className="zulu-plugin__notice-logo-input"
              type="file"
              accept=".png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif"
              disabled={!emailsOn || logoBusy}
              onChange={(event) => {
                const file = event.target.files && event.target.files[0];
                void uploadLogo(file || null);
              }}
            />
            <button
              type="button"
              className="zulu-plugin__notice-logo-btn"
              disabled={!emailsOn || logoBusy}
              onClick={() => logoInputRef.current?.click()}
            >
              {logoBusy
                ? "Working…"
                : settings.logoUrl
                  ? "Replace logo"
                  : "Upload logo"}
            </button>
            {settings.logoUrl ? (
              <button
                type="button"
                className="zulu-plugin__notice-logo-btn is-muted"
                disabled={!emailsOn || logoBusy}
                onClick={() => void removeLogo()}
              >
                Remove logo
              </button>
            ) : null}
          </div>
        </div>
        <p className="zulu-plugin__settings-help">
          Optional. PNG, JPG, or GIF. Max {maxKb} KB. Max{" "}
          {logoLimits.maxWidthPx}×{logoLimits.maxHeightPx} px (recommended{" "}
          {logoLimits.recommendedWidthPx}×{logoLimits.recommendedHeightPx} px).
          Without a logo, emails still send.
        </p>
        {logoError ? (
          <p className="zulu-plugin__settings-status is-error" role="alert">
            {logoError}
          </p>
        ) : null}
        <label className="zulu-plugin__field-label" htmlFor="reply-to-email">
          Reply-To Email
        </label>
        <input
          id="reply-to-email"
          className="zulu-plugin__notice-input"
          type="email"
          autoComplete="email"
          placeholder={settings.storeEmail || "store@example.com"}
          value={settings.replyToEmail}
          disabled={!emailsOn}
          onChange={(event) => {
            setSettings((current) => ({
              ...current,
              replyToEmail: event.target.value,
            }));
            setStatus("idle");
          }}
        />
        <p className="zulu-plugin__settings-help">
          {settings.storeEmail
            ? `Defaults to your shop email (${settings.storeEmail}). `
            : "Defaults to your shop or owner email when available. "}
          You can change it here. Customers reply to this address.
        </p>

        <div className="zulu-plugin__notice-template-head">
          <label
            className="zulu-plugin__field-label"
            htmlFor="delivery-email-template"
          >
            Customer email body
          </label>
          <button
            type="button"
            className="zulu-plugin__notice-reset"
            disabled={!emailsOn}
            onClick={() => {
              setSettings((current) => ({
                ...current,
                replyToEmail: current.replyToEmail.trim() || current.storeEmail,
                deliveryEmailTemplate: DEFAULT_DELIVERY_EMAIL_TEMPLATE,
              }));
              setStatus("idle");
            }}
          >
            Restore default
          </button>
        </div>
        <EmailBodyEditor
          value={settings.deliveryEmailTemplate}
          disabled={!emailsOn}
          storeName={
            settings.storeName.trim() ||
            fallbackStoreName.trim() ||
            ""
          }
          replyToEmail={settings.replyToEmail || settings.storeEmail}
          onChange={(deliveryEmailTemplate) => {
            setSettings((current) => ({
              ...current,
              deliveryEmailTemplate,
            }));
            setStatus("idle");
          }}
        />
        <p className="zulu-plugin__settings-help">
          Edit the message text. Store name and reply-to show as real values.
          Order Status and Order Number are orange labels and fill in
          automatically.
        </p>
      </div>
      {!emailsOn ? (
        <p className="zulu-plugin__settings-help">
          Customer order emails are off. Shoppers will not get delivered, dropped,
          return, or reattempt messages for this store.
        </p>
      ) : null}
      {!sameNotificationSettings(settings, savedSettings) ? (
        <form onSubmit={saveSettings}>
          <div className="zulu-plugin__settings-actions">
            <button
              type="submit"
              className="zulu-plugin__settings-save"
              disabled={status === "saving" || logoBusy}
            >
              {status === "saving" ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      ) : null}
      {status === "saved" ? (
        <p className="zulu-plugin__settings-status is-ok">
          {emailsOn
            ? "Saved. Customer emails use your logo, message, and Reply-To address."
            : "Saved. Customer order emails are off."}
        </p>
      ) : null}
      {status === "error" ? (
        <p className="zulu-plugin__settings-status is-error">{error}</p>
      ) : null}
    </div>
  );
};

export default MerchantSettings;
